/* EventsComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./EventsRenderer");
const _ = require("lodash");

class EventsComponent extends EventEmitter {
    constructor(
        OrganizationService,
        userSession,
        uiUtils,
        steve,
        logger,
        SyslogNg,
        ACS,
        ACSPrefs,
        WorkflowType,
        userFeedback
    ) {
        super();
        this._userSession = userSession;
        this._syslog = undefined;
        this._uiUtils = uiUtils;
        this._steve = steve;
        this._logger = logger;
        this._SyslogEntity = SyslogNg;
        this._syslogEntityInstance = undefined;
        this._updatedSyslog = {};
        this._isDirty = false;
        this._authorizedActions = undefined;
        this._ACS = ACS;
        this._acsEventsListEntity = undefined;
        this._ACSPrefs = ACSPrefs;
        this._WorkflowType = WorkflowType;
        this._workflowTypes = undefined;
        this._acsPrefsEntity = undefined;
        this._acsConf = undefined;
        this._acsEvents = undefined;
        this._acsEventConf = undefined;
        this._updatedAcsEventConf = undefined;
        this._debouncedAcsPropertiesUpdated = _.debounce(
            this._onAcsPropertiesUpdated.bind(this),
            150
        ).bind(this);
        this._userFeedback = userFeedback;
        this._renderer = new Renderer(this, this._uiUtils);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._acsPrefsEntity.on("PropertiesUpdated", this._debouncedAcsPropertiesUpdated);
    }

    _unregisterListeners() {
        this._acsPrefsEntity.removeListener(
            "PropertiesUpdated",
            this._debouncedAcsPropertiesUpdated
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        const promArr = [
            this._renderer._shutdown(),
            this._acsPrefsEntity.shutdown(),
            this._syslogEntityInstance.shutdown(),
        ];
        return Promise.all(promArr).then(
            () => {
                this._userSession = undefined;
                this._SyslogEntity = undefined;
                this._syslog = undefined;
                this._updatedSyslog = undefined;
                this._uiUtils = undefined;
                this._steve = undefined;
                this._logger = undefined;
                this._renderer = undefined;
                this._isDirty = undefined;
                this._syslogEntityInstance = undefined;
                this._authorizedActions = undefined;
                this._acsEvents = undefined;
                this._acsConf = undefined;
                this._acsPrefsEntity = undefined;
                this._acsEventConf = undefined;
                this._updatedAcsEventConf = undefined;
                this._acsEventsListEntity = undefined;
                this._userFeedback = undefined;
            },
            (err) => {
                console.log("Error shutting down EventsComponent: ", err);
            }
        );
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    whenModelIsReady() {
        // this._userSession.activeOrganization.id
        const orgId = this._userSession.activeOrganization.id;
        let syslog;
        return Promise.all([
            this._SyslogEntity.retrieveSyslogNg(orgId),
            this._ACS.loadAutoCaptureEvents(orgId),
            this._ACSPrefs.retrieveAutoCapturePrefs(orgId),
            this._WorkflowType.startAscendWorkflowTypesFeed(orgId, {workflowType: "PRODUCTION"}),
        ])
            .then((values) => {
                syslog = values[0];
                this._acsPrefsEntity = values[2];
                this._acsEventsListEntity = values[1][0];
                this._WorkflowTypeFeed = values[3];
                this._isRPALicenseAvailable = this._steve.licenseRestriction.checkLicense("RPA");
                // this._isRPALicenseAvailable = false; // for testing
                return this._WorkflowTypeFeed.whenInitialized();
            })
            .then(() => {
                if (
                    this._isRootOrg(orgId) &&
                    !_.get(this._acsPrefsEntity, "pristineProperties.enabled", false)
                ) {
                    this._acsPrefsEntity.setEnabled(true);
                    return this._acsPrefsEntity.update();
                }
                return Promise.resolve(false);
            })
            .then((entity) => {
                if (entity) {
                    this._acsPrefsEntity.clearPendingPatches();
                    return Promise.resolve();
                }
                return Promise.resolve();
            })
            .then(() => {
                this._setAcsEventConfVars();
                this._setData(syslog);
                this._authorizedActions = this._userSession.user.authorizedActions;
                this._registerListeners();
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err, "EventsComponent.whenModelIsReady() error.");
                return Promise.reject(err);
            });
    }

    _setAcsEventConfVars() {
        this._acsConf = Object.assign(
            {},
            _.get(this._acsPrefsEntity, "pristineProperties", undefined)
        );
        if (this._acsConf.enabled) {
            this._acsEventConf = Object.assign({}, _.get(this._acsConf, "ownOrgEvents", {}));
            this._updatedAcsEventConf = Object.assign({}, _.get(this._acsConf, "ownOrgEvents", {}));
        } else {
            this._acsEventConf = Object.assign({}, _.get(this._acsConf, "ascendantOrgEvents", {}));
            this._updatedAcsEventConf = Object.assign(
                {},
                _.get(this._acsConf, "ascendantOrgEvents", {})
            );
        }
        this._acsEvents = this._convertToSlick(
            _.get(this._acsEventsListEntity, "pristineProperties.events", []),
            _.get(this._WorkflowTypeFeed, "cache", {}),
            this._isRPALicenseAvailable
        );
    }

    _convertToSlick(data, wfEntities, isRpaAvailable = false) {
        let category;
        const formattedData = [];
        data.forEach((item, idx) => {
            if (category !== item.category) {
                category = item.category;
                formattedData.push({
                    category,
                    name: category,
                    title: true,
                    id: category + "-1",
                });
            }
            formattedData.push(
                Object.assign({}, item, {
                    title: false,
                    id: category + idx,
                    workflows: wfEntities,
                    isRpaAvailable,
                })
            );
        });
        return this._setConf(formattedData);
    }

    _setConf(data) {
        if (!this._acsConf) {
            return data;
        }
        let values;
        if (this._acsConf.enabled) {
            values = this._acsConf.ownOrgEvents;
        } else {
            values = this._acsConf.ascendantOrgEvents;
        }
        data.forEach((item) => {
            if (item.eventKey) {
                if (values[item.eventKey] && typeof values[item.eventKey] === "object") {
                    if (values[item.eventKey].workflowId) {
                        item.value = false;
                    } else {
                        item.value = true;
                    }
                    item.workflow = values[item.eventKey].workflowId;
                } else {
                    item.value = values[item.eventKey]; // fallback for older entites where only snapshot is available
                }
            }
        });

        return data;
    }

    organizationChanged(orgObj) {
        this.emit("SyslogOrganizationChanged", orgObj);
    }

    resetConfigData(orgObj) {
        const promArr = [];
        const feedPromArr = [
            this._syslogEntityInstance.shutdown(),
            this._acsPrefsEntity.shutdown(),
            this._WorkflowTypeFeed.shutdown(),
        ];
        promArr.push(this._SyslogEntity.retrieveSyslogNg(orgObj.id));
        promArr.push(this._ACSPrefs.retrieveAutoCapturePrefs(orgObj.id));
        promArr.push(this._WorkflowType.startAscendWorkflowTypesFeed(orgObj.id));
        this._unregisterListeners();
        let syslog;
        return Promise.all(feedPromArr)
            .then(() => Promise.all(promArr))
            .then((values) => {
                syslog = values[0];
                this._acsPrefsEntity = values[1];
                this._WorkflowTypeFeed = values[2];
                this._isRPALicenseAvailable = this._steve.licenseRestriction.checkLicense("RPA");
                // this._isRPALicenseAvailable = false; // for testing
                return this._WorkflowTypeFeed.whenInitialized();
            })
            .then(() => {
                this._setData(syslog);
                if (
                    this._isRootOrg(orgObj.id) &&
                    !_.get(this._acsPrefsEntity, "pristineProperties.enabled", false)
                ) {
                    this._acsPrefsEntity.setEnabled(true);
                    return this._acsPrefsEntity.update();
                }
                this._registerListeners();
                return Promise.resolve(false);
            })
            .then((entity) => {
                if (entity) {
                    this._acsPrefsEntity.clearPendingPatches();
                    return Promise.resolve();
                }
                return Promise.resolve();
            })
            .then(() => {
                this._setAcsEventConfVars();
                this._isDirty = false;
            })
            .catch((err) => {
                this._logger.warn(err, "EventsComponent.resetApiConfigData() error.");
                return Promise.reject(err);
            });
    }

    _setData(syslog) {
        this._syslogEntityInstance = syslog;
        this._syslog = {
            enabled: this._syslogEntityInstance.enabled,
            port: this._syslogEntityInstance.port,
            userIncludeList: this._syslogEntityInstance.userIncludeList,
            userExcludeList: this._syslogEntityInstance.userExcludeList,
        };
        this._updatedSyslog = Object.assign({}, this._syslog);
    }

    _isRootOrg(orgId) {
        const oid = orgId || this.activeOrgId;
        if (this._userSession && this._userSession._userEntity) {
            return !_.get(
                this._userSession._userEntity.authorizedTenants,
                `${oid}.providerName`,
                false
            );
        }
    }

    _onAcsPropertiesUpdated() {
        this._setAcsEventConfVars();
        this.emit("AcsEventsUpdated");
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    save() {
        this._syslogEntityInstance.setEnabled(this._updatedSyslog.enabled);
        this._syslogEntityInstance.setUserIncludeList(this._updatedSyslog.userIncludeList);
        this._syslogEntityInstance.setUserExcludeList(this._updatedSyslog.userExcludeList);
        return Promise.resolve()
            .then(() => {
                if (this._syslogEntityInstance.isDirty()) {
                    return this._syslogEntityInstance.update().then(() => {
                        this._syslog = Object.assign({}, this._updatedSyslog);
                        return Promise.resolve(this._syslog);
                    });
                }
                return Promise.resolve(this._syslog);
            })
            .then(() => {
                if (
                    !_.isEqual(this._acsEventConf, this._updatedAcsEventConf) ||
                    this._acsPrefsEntity.isDirty()
                ) {
                    if (this._acsPrefsEntity.getEnabled()) {
                        this._acsPrefsEntity.setOwnOrgEvents(this._updatedAcsEventConf);
                    }
                    return this._acsPrefsEntity.update();
                }
                return Promise.resolve(false);
            })
            .then((val) => {
                if (!val) {
                    return Promise.resolve();
                }
                this._acsPrefsEntity.clearPendingPatches();
                return Promise.resolve();
            })
            .then(() => {
                this._isDirty = false;
                return Promise.resolve();
            })
            .catch((err) => Promise.reject(err));
    }

    saveAcs() {
        return this._acsPrefsEntity.update().then(() => {
            this._acsPrefsEntity.clearPendingPatches();
            return Promise.resolve();
        });
    }

    cancel() {
        return Promise.resolve().then(() => {
            this._updatedSyslog = Object.assign({}, this._syslog);
            this._updatedAcsEventConf = Object.assign({}, this._acsEventConf);
            this._acsPrefsEntity.clearPendingPatches();
            this._setAcsEventConfVars();
            this._isDirty = false;
            return this._updatedSyslog;
        });
    }
    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = EventsComponent;

EventsComponent.$groupName = "Organization";

EventsComponent.$displayName = "Events";

EventsComponent.$inject = [
    "$teve.services.instance.Organization",
    "$teve.userSession",
    "$teve.ui.UiUtils",
    "$teve.core",
    "$teve.logger",
    "$teve.entities.SyslogNg",
    "$teve.entities.AutoCaptureEvents",
    "$teve.entities.AutoCapturePrefs",
    "$teve.entities.WorkflowType",
    "$teve.ui.userFeedback",
];
